import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { IMAGES } from "../data/content";

const BENEFITS = [
  { n: "01", t: "Konsistenz über Einheiten", c: "Eine Handschrift, wiederholbar über dutzende Einheiten — bei gezielt gesteuerter Differenzierung." },
  { n: "02", t: "Skalierbare Umsetzung", c: "Planung, Logistik und Rollout sind auf Serienprojekte ausgelegt. Ein Prozess, viele Einheiten." },
  { n: "03", t: "Reduzierter Koordinationsaufwand", c: "Ein Ansprechpartner, ein Konzeptsystem, klare Entscheidungspunkte — statt paralleler Einzellösungen." },
  { n: "04", t: "Wiedererkennung", c: "Objekte werden zu einer Marke — durch konsistente Gestaltung, nicht durch Logo-Platzierung." },
];

const USE_CASES = [
  { t: "Ferienparks & Anlagen", c: "20–200+ Einheiten mit klarer Ausprägungslogik." },
  { t: "Apartmentbetreiber", c: "Langfristig vermietete Einheiten mit Wiedererkennungsprofil." },
  { t: "Projektentwickler", c: "Staging & Erstausstattung im Rollout." },
  { t: "Serviced Apartments", c: "Gastfreundliche, langlebige, ruhige Einrichtung." },
  { t: "Boardinghouses (klein)", c: "Operative Effizienz bei hoher Aufenthaltsqualität." },
  { t: "Retreat-Formate", c: "Kleine Ensembles mit differenzierten Unit-Typen." },
];

export default function Gewerblich() {
  useDocumentTitle(
    "Gewerblich — Skalierbare Einrichtungskonzepte für Betreiber & Ferienparks",
    "Konsistente Einrichtungen für mehrere Einheiten. Livinora übersetzt Stilserien in ein skalierbares System für Betreiber, Ferienparks und Projektentwickler."
  );

  return (
    <div data-testid="page-gewerblich">
      {/* Hero */}
      <section className="pt-28 sm:pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel index="∞" text="Gewerblich" />
            </Reveal>
            <Reveal delay={60}>
              <h1 className="display-xl mt-8 text-[44px] sm:text-[60px] md:text-[80px] lg:text-[96px] text-balance">
                Viele Einheiten.
                <br className="hidden sm:block" />
                Eine Handschrift.
              </h1>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora prose-livinora--lead mt-9 max-w-xl">
                Für Betreiber, Entwickler und Parks übersetzen wir unser Konzept in ein
                skalierbares System: konsistent über Einheiten, differenziert in der Ausprägung
                und effizient im Rollout.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/konfigurator?audience=gewerblich"
                  className="btn-livinora btn-primary btn-magnetic"
                  data-testid="gewerblich-cta"
                >
                  Projekt konfigurieren <span>→</span>
                </Link>
                <Link to="/broschuere" className="btn-livinora btn-ghost">
                  Gewerbe-Broschüre
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal variant="mask">
              <div className="media-wrap aspect-[4/5]">
                <img src={IMAGES.commercial} alt="Gewerblich" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-pad bg-[color:var(--livinora-paper-warm)] border-y border-[color:var(--livinora-hairline)]">
        <div className="container-livinora">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <Reveal>
                <SectionLabel index="01" text="Warum Livinora" />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="serif text-4xl md:text-5xl mt-8 leading-[1.05]">
                  Vier Gründe für die Skalierung mit uns.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <Reveal delay={140}>
                <p className="prose-livinora">
                  Unsere Systemlogik ist von Anfang an auf Wiederholbarkeit gebaut — ohne in
                  Uniformität zu kippen. Einheiten sollen erkennbar zusammengehören und trotzdem
                  eigenständig wirken.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-[color:var(--livinora-hairline)]">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.n} delay={i * 80}>
                <div
                  className={`p-8 md:p-10 border-b border-[color:var(--livinora-hairline)] ${i % 2 === 0 ? "md:border-r" : ""} h-full`}
                >
                  <span className="micro-label">{b.n}</span>
                  <h3 className="serif text-2xl md:text-3xl mt-3 leading-tight">{b.t}</h3>
                  <p className="mt-4 text-[color:var(--livinora-muted)] leading-relaxed">{b.c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="section-pad">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <SectionLabel index="02" text="Anwendungen" />
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-4xl md:text-5xl mt-8 leading-[1.05]">
                Für welche Formate wir arbeiten.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7 max-w-sm">
                Von Ferienparks bis Boardinghouses — überall dort, wo Konsistenz, Wiederholbarkeit
                und Aufenthaltsqualität zusammenkommen müssen.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/kontakt" className="btn-livinora btn-primary btn-magnetic" data-testid="gewerblich-anwendung-cta">
                  Persönliche Abstimmung <span>→</span>
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <ul className="divide-y divide-[color:var(--livinora-hairline)] border-y border-[color:var(--livinora-hairline)]">
              {USE_CASES.map((u, i) => (
                <Reveal key={u.t} delay={i * 50}>
                  <li className="flex items-start justify-between gap-6 sm:gap-10 py-6">
                    <div className="flex items-start gap-5 sm:gap-6">
                      <span className="micro-label pt-2 shrink-0">0{i + 1}</span>
                      <div>
                        <div className="serif text-xl sm:text-2xl leading-tight">{u.t}</div>
                        <div className="text-[color:var(--livinora-muted)] mt-1.5 text-sm sm:text-base">{u.c}</div>
                      </div>
                    </div>
                    <Link to="/kontakt" className="btn-line hidden md:inline-flex mt-1 shrink-0">Anfragen</Link>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
